import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { WorkOrder } from "features/WorkOrders/types/work-order.types";
import { ColumnDef } from "@tanstack/react-table";
import { getWorkOrders } from "features/WorkOrders/services/WorkOrderService";
import { invalidateReadyForBillingWorkOrderCount } from "features/WorkOrders/workOrderReadyForBillingCountInvalidate";
import {
  WorkOrderSourceTypeLabelMap,
  WorkOrderStatus,
  workOrderAllowsMaterialRequest,
  workOrderStatusMap,
} from "features/WorkOrders/constants/work-order.constants";

import StatusBadge from "components/common/StatusBadge";
import {
  CodeTitleColumns,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateTime } from "components/common/Date";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { workOrderPermissionFlags } from "features/WorkOrders/utils/workOrderPermissions";
import { MaterialRequestFormSource } from "features/MaterialRequests/constants/material-request.constants";
import { getMaterialRequestFromJobPath } from "features/MaterialRequests/utils/materialRequestFromJobPaths";
import WorkOrderLifecycleModals, {
  type WorkOrderLifecycleIntent,
} from "features/WorkOrders/components/WorkOrderLifecycleModals";
import {
  getWorkOrderLifecycleVisibility,
  workOrderLifecycleAnyVisible,
} from "features/WorkOrders/utils/work-order-lifecycle.utils";
import WorkOrderListFilters, {
  countWorkOrderListFiltersActive,
  emptyWorkOrderListFilters,
  workOrderListFiltersToListParams,
  type WorkOrderListFiltersState,
} from "./WorkOrderListFilters";
import { useProfile } from "features/Authentication/hooks/useProfile";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import WorkOrderAssigneesAvatarGroup from "features/WorkOrders/components/WorkOrderAssigneesAvatarGroup";

function isTerminalWorkOrderStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  return s === WorkOrderStatus.COMPLETED || s === WorkOrderStatus.CANCELLED;
}

export const WorkOrdersTable = () => {
  const { user, isSuperAdmin } = useProfile();
  const { can } = useAccess();
  const canReadWo = can(Permissions.WORK_ORDER.READ);
  const canUpdateWo = can(Permissions.WORK_ORDER.UPDATE);
  const canCreateMaterialRequest = can(Permissions.MATERIAL_REQUEST.CREATE);
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const tableRef = useRef<() => void>(null);
  const [lifecycle, setLifecycle] = useState<{
    wo: WorkOrder;
    intent: WorkOrderLifecycleIntent;
  } | null>(null);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormWo, setAssignFormWo] = useState<WorkOrder | undefined>();
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);

  const onClickAssign = useCallback((record: WorkOrder) => {
    setAssignFormWo(record);
    setAssignFormIsReassign(false);
    setAssignFormOpen(true);
  }, []);

  const onClickReassign = useCallback((record: WorkOrder) => {
    setAssignFormWo(record);
    setAssignFormIsReassign(true);
    setAssignFormOpen(true);
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormIsReassign(false);
  }, []);
  const [listFilters, setListFilters] = useState<WorkOrderListFiltersState>(
    () => emptyWorkOrderListFilters(),
  );

  const listRequestParams = useMemo(
    () => workOrderListFiltersToListParams(listFilters),
    [listFilters],
  );

  const filtersBadgeCount = useMemo(
    () => countWorkOrderListFiltersActive(listFilters),
    [listFilters],
  );

  const clearListFilters = useCallback(() => {
    setListFilters(emptyWorkOrderListFilters());
  }, []);

  const columns: ColumnDef<WorkOrder>[] = useMemo(
    () => [
      IndexColumn<WorkOrder>(),
      ...CodeTitleColumns<WorkOrder>({
        titleHeader: "Work Order",
        accessorKey: "code",
        resolve: (record) => ({
          code: record.code || "—",
          title: record.title?.trim(),
          to: canReadWo ? `/work-orders/${record.id}` : null,
        }),
      }),

      SourceColumn<WorkOrder>({
        resolve: (row) =>
          resolveMappedSourceCell(row.sourceType, row.sourceId, {
            typeLabelMap: WorkOrderSourceTypeLabelMap,
            source: row.source,
            linkRequiresCode: true,
          }),
      }),

      {
        header: "Assigned",
        id: "assigned",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const isTerminal = isTerminalWorkOrderStatus(record.status);
          return (
            <WorkOrderAssigneesAvatarGroup
              workOrder={record}
              workAssignments={record.workAssignments}
              canReadDetail={canReadWo}
              canAssignMore={canCreateAssignment && !isTerminal}
              onOpenAssign={onClickAssign}
            />
          );
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={workOrderStatusMap} />;
        },
      },

      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },

      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const rowFlags = workOrderPermissionFlags(user, record, isSuperAdmin);
          const showView = canReadWo;
          const showEdit = canUpdateWo && rowFlags.canEdit;
          const showAssign =
            (canCreateAssignment || canUpdateAssignment) &&
            !isTerminalWorkOrderStatus(record.status);
          const showRequestMaterials =
            canCreateMaterialRequest &&
            workOrderAllowsMaterialRequest(record.status);
          const lifecycleVis = getWorkOrderLifecycleVisibility(record, can);
          const showLifecycle = workOrderLifecycleAnyVisible(lifecycleVis);
          if (
            !showView &&
            !showEdit &&
            !showAssign &&
            !showRequestMaterials &&
            !showLifecycle
          ) {
            return <span className="text-muted small">—</span>;
          }
          const hasAnyBeforeMaterials =
            showView || showEdit || showAssign || showLifecycle;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Work order actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {showView ? (
                  <DropdownItem tag={Link} to={`/work-orders/${record.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showView ? (
                  <DropdownItem
                    tag={Link}
                    to={`/work-orders/${record.id}?tab=tasks`}
                  >
                    <i className="ri-task-line align-bottom me-2 text-muted" />
                    View tasks
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/work-orders/edit/${record.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showAssign ? (
                  <>
                    {showView || showEdit ? <DropdownItem divider /> : null}
                    {canCreateAssignment ? (
                      <DropdownItem onClick={() => onClickAssign(record)}>
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canUpdateAssignment ? (
                      <DropdownItem onClick={() => onClickReassign(record)}>
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {showLifecycle ? (
                  <>
                    {showView || showEdit ? <DropdownItem divider /> : null}
                    {lifecycleVis.start ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "start" },
                          })
                        }
                      >
                        <i className="ri-play-circle-line align-bottom me-2 text-muted" />
                        Start work
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.hold ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({ wo: record, intent: { kind: "hold" } })
                        }
                      >
                        <i className="ri-pause-circle-line align-bottom me-2 text-muted" />
                        Hold
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.resume ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "resume" },
                          })
                        }
                      >
                        <i className="ri-play-circle-line align-bottom me-2 text-muted" />
                        Resume
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.submitCompletion ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "submitCompletion" },
                          })
                        }
                      >
                        <i className="ri-checkbox-circle-line align-bottom me-2 text-muted" />
                        Submit completion
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.approveForBilling ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "approveForBilling" },
                          })
                        }
                      >
                        <i className="ri-check-double-line align-bottom me-2 text-muted" />
                        Approve for billing
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.submitBilling ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "submitBilling" },
                          })
                        }
                      >
                        <i className="ri-bill-line align-bottom me-2 text-muted" />
                        Submit billing
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.close ? (
                      <DropdownItem
                        onClick={() =>
                          setLifecycle({
                            wo: record,
                            intent: { kind: "close" },
                          })
                        }
                      >
                        <i className="ri-lock-line align-bottom me-2 text-muted" />
                        Close
                      </DropdownItem>
                    ) : null}
                    {lifecycleVis.rejectCompletion || lifecycleVis.cancel ? (
                      <>
                        <DropdownItem divider />
                        {lifecycleVis.rejectCompletion ? (
                          <DropdownItem
                            className="text-danger"
                            onClick={() =>
                              setLifecycle({
                                wo: record,
                                intent: { kind: "rejectCompletion" },
                              })
                            }
                          >
                            <i className="ri-close-circle-line align-bottom me-2" />
                            Reject completion
                          </DropdownItem>
                        ) : null}
                        {lifecycleVis.cancel ? (
                          <DropdownItem
                            className="text-danger"
                            onClick={() =>
                              setLifecycle({
                                wo: record,
                                intent: { kind: "cancel" },
                              })
                            }
                          >
                            <i className="ri-close-circle-line align-bottom me-2" />
                            Cancel
                          </DropdownItem>
                        ) : null}
                      </>
                    ) : null}
                  </>
                ) : null}
                {showRequestMaterials ? (
                  <>
                    {hasAnyBeforeMaterials ? <DropdownItem divider /> : null}
                    <DropdownItem
                      tag={Link}
                      to={getMaterialRequestFromJobPath(
                        MaterialRequestFormSource.WORK_ORDER,
                        record.id,
                      )}
                    >
                      <i className="ri-inbox-archive-line align-bottom me-2 text-muted" />
                      Request materials
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      canReadWo,
      canUpdateWo,
      canCreateAssignment,
      canUpdateAssignment,
      canCreateMaterialRequest,
      can,
      user,
      isSuperAdmin,
      onClickAssign,
      onClickReassign,
    ],
  );

  return (
    <React.Fragment>
      <WorkAssignmentForm
        show={assignFormOpen}
        sourceType={WorkAssignmentSourceType.WORK_ORDER}
        sourceId={assignFormWo?.id ?? 0}
        sourceLabel={
          assignFormWo?.code
            ? `Work Order ${assignFormWo.code}`
            : assignFormWo?.title?.trim()
              ? `Work Order: ${assignFormWo.title}`
              : `Work Order #${assignFormWo?.id ?? ""}`
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={() => {
          closeAssignmentForm();
          tableRef.current?.();
        }}
      />

      <WorkOrderLifecycleModals
        workOrder={lifecycle?.wo ?? null}
        intent={lifecycle?.intent ?? null}
        onFinished={() => setLifecycle(null)}
        onSuccess={() => {
          void tableRef.current?.();
          invalidateReadyForBillingWorkOrderCount();
        }}
      />

      <TableContainer
        columns={columns}
        fetchApi={getWorkOrders}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        requestParams={listRequestParams}
        SearchPlaceholder="Search by code, title, source…"
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <WorkOrderListFilters
              filters={listFilters}
              onChange={setListFilters}
            />
            {filtersBadgeCount > 0 && (
              <button
                type="button"
                className="btn btn-soft-danger d-flex align-items-center gap-1"
                onClick={clearListFilters}
              >
                <i className="ri-close-circle-line" />
                Clear
              </button>
            )}
          </div>
        }
      />
    </React.Fragment>
  );
};
